import React, { useEffect, useState, useCallback } from "react";
import styled from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  AlertIcon,
  Button,
  ButtonLink,
  ButtonRound,
  ContentPage,
  DatePicker,
  DescriptionList,
  DrawerPanel,
  DropdownAction,
  ExportIcon,
  FullTable,
  ModalForm,
  NoResults,
  Notice,
  Presence,
  Row,
  SelectorPageStatus,
  StatePill,
  Text,
  Tooltip,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "../../hooks/useService";
import ValueInputModal from "../ValueInputModal";
import TaxInvoiceUploadModal from "../TaxInvoiceUploadModal";
import MarkPaidModal from "../MarkPaidModal";
import ApproveInvoiceModal from "../ApproveInvoiceModal";
import RejectInvoiceModal from "../RejectInvoiceModal";
import EditScheduleModal from "../EditScheduleModal";
import DeleteScheduleModal from "../DeleteScheduleModal";
import { getUploadBlockReason, scheduleHasValue } from "../../helpers";
import {
  rangeIntersectsContractAndProjectPeriod,
  firstDayOfMonth,
  lastDayOfMonth,
} from "../../helpers/contractPeriod";
import { schedulePeriod, formatPeriodLabel } from "../../helpers/competencePeriod";
import { downloadTaxInvoice } from "../../helpers/taxInvoiceFile";
import { bankDataRoute } from "../../helpers/bankData";
import {
  fixedAdjustmentCompetence,
  isCompetenceOpen,
} from "../../helpers/fixedAdjustmentCompetence";
import { DomainStatus, escapeHtml, formatBRL, formatCompetencia, formatDate, formatHours } from "../ow";

const FIXED_MODE = "FIXED_WITH_HOUR_ADJUSTMENT";
const PAGE_SIZE = 10;

const SCHEDULE_STATUS = { PENDING: "pendente", PAID: "pago", OVERDUE: "atrasado", CANCELLED: "cancelado" };
const INVOICE_STATUS = { PENDING: "pendente", APPROVED: "aprovada", REJECTED: "recusada", PAID: "paga" };

// Pílulas do filtro de status: uma por vez, "Todos" sem filtro
const STATUS_DATA = [
  { label: "Todos", value: "" },
  { label: "Pendente", value: "PENDING" },
  { label: "Pago", value: "PAID" },
  { label: "Atrasado", value: "OVERDUE" },
  { label: "Cancelado", value: "CANCELLED" },
];

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${(p) => p.$align || "stretch"};
  gap: ${(p) => p.$gap || "16px"};
`;

const Inline = styled.div`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const CompetenceFields = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
`;

// Os ícones da lib não têm tamanho próprio
const IconBox = styled.span`
  display: inline-flex;
  color: ${({ theme }) => theme.colors.E400};

  svg {
    width: 20px;
    height: 20px;
  }
`;

const competenceOf = (schedule) => ({ month: Number(schedule.reference_month), year: Number(schedule.reference_year) });

/** Horas decimais da API ("167.5") no padrão do design system ("167h30"); nunca decimal. */
const hrs = (value) => formatHours((Number(value) || 0) * 60);

/**
 * Conta corrente das horas (padrão com dia de fechamento). Os números dela (medidas e
 * cobradas são acumulados do contrato; o adiantamento é previsão) não vão para a tela:
 * só "Horas no pagamento", com a regra explicada no tooltip da coluna Horas.
 */
function hasLedger(schedule) {
  return (
    schedule.paid_hours !== null && schedule.paid_hours !== undefined && schedule.measured_hours !== null && schedule.measured_hours !== undefined
  );
}

const LEDGER_RULE =
  "Entram as horas aprovadas até o dia de fechamento e a jornada prevista até o fim do mês. O que foi adiantado no mês anterior é descontado aqui.";

/** Botão dentro de uma linha clicável: o clique nele não abre o drawer da competência. */
const stopRowClick = (event) => event.stopPropagation();

/**
 * Aba de Cronograma de Pagamento com NFs inline.
 * Compartilhado entre Admin e Employee.
 *
 * Props:
 * - contractId: number
 * - contract: object (dados do contrato)
 * - isAdmin: boolean (visão da empresa em vez da do prestador)
 * - canManage: boolean (gerar, informar valor, aprovar/rejeitar NF, marcar pago, recalcular,
 *   editar e excluir; padrão = isAdmin. Quem só tem "Ver cronogramas" lê sem essas ações)
 * - canDownloadInvoice: boolean (baixar o arquivo da NF; padrão true — ADM e o próprio prestador)
 * - noBank: boolean (se EMP não tem dados bancários — apenas para EMP)
 */
export default function PaymentScheduleTab({
  contractId,
  contract,
  isAdmin,
  canManage: canManageProp,
  canDownloadInvoice = true,
  noBank,
}) {
  const canManage = canManageProp ?? isAdmin;
  const navigate = useNavigate();
  const location = useLocation();
  const paymentScheduleService = useService("payment-schedules");
  const taxInvoicesService = useService("taxInvoices");
  const valueInputService = useService("contract-value-inputs");

  const [schedules, setSchedules] = useState([]);
  const [totalRegisters, setTotalRegisters] = useState(0);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: PAGE_SIZE });
  const [statusFilter, setStatusFilter] = useState(STATUS_DATA[0]);
  const [loadingAction, setLoadingAction] = useState(false);

  // Detalhe da competência (composição do valor e nota), no drawer. O cronograma
  // fica guardado depois de fechar, para o conteúdo não sumir na animação de saída.
  const [detailSchedule, setDetailSchedule] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // Value Input modal
  const [showValueInput, setShowValueInput] = useState(false);

  // NF Upload modal (EMP)
  const [showNfUpload, setShowNfUpload] = useState(false);
  const [selectedScheduleForUpload, setSelectedScheduleForUpload] = useState(null);

  // Mark as Paid modal
  const [selectedScheduleForPaid, setSelectedScheduleForPaid] = useState(null);

  // Approve / Reject NF
  const [nfToApprove, setNfToApprove] = useState(null);
  const [selectedNfForReject, setSelectedNfForReject] = useState(null);

  // Edit schedule modal
  const [selectedScheduleForEdit, setSelectedScheduleForEdit] = useState(null);

  // Delete schedule modal
  const [selectedScheduleForDelete, setSelectedScheduleForDelete] = useState(null);

  // Generate schedule modal
  const today = new Date();
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [genStart, setGenStart] = useState({ month: today.getMonth() + 1, year: today.getFullYear() });
  const [genEnd, setGenEnd] = useState({ month: today.getMonth() + 1, year: today.getFullYear() });
  const [generateError, setGenerateError] = useState("");

  const fetchSchedules = useCallback(async () => {
    setLoading(true);
    try {
      let query = `contract_id=${contractId}&page=${pagination.pageIndex + 1}&limit=${pagination.pageSize}&order=reference_year:DESC,reference_month:DESC`;
      // Visão da empresa: sem scope=company o back devolve só os cronogramas de quem chama
      if (isAdmin) query = `scope=company&${query}`;
      if (statusFilter.value) query += `&status=${statusFilter.value}`;
      const result = await paymentScheduleService.list(query);
      if (result?.data) {
        setTotalRegisters(result.data.totalRegisters || 0);
        setSchedules(result.data.list || []);
      }
    } catch (err) {
      console.error(err);
      toast.error("Erro ao carregar cronogramas");
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId, pagination.pageIndex, pagination.pageSize, statusFilter]);

  useEffect(() => {
    if (contractId) fetchSchedules();
  }, [contractId, fetchSchedules]);

  function changeStatusFilter(option) {
    setStatusFilter(option);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }

  function openDetail(schedule) {
    setDetailSchedule(schedule);
    setDetailOpen(true);
  }

  function handleDownloadNf(invoice) {
    return downloadTaxInvoice(taxInvoicesService, invoice);
  }

  function handleMarkAsPaid(schedule) {
    // Sem valor definido não há o que dar baixa: o back recusa e antes disso
    // o actual_value iria como NaN
    if (!scheduleHasValue(schedule)) {
      toast.error("Este cronograma ainda não tem valor definido. Defina o valor antes de registrar o pagamento.");
      return;
    }
    setSelectedScheduleForPaid(schedule);
  }

  async function handleRecalculateHourly(scheduleId) {
    setLoadingAction(true);
    try {
      const result = await paymentScheduleService.recalculateHourly(scheduleId);
      if (result && !result.error) {
        toast.success(result.msg || "Horas recalculadas!");
        fetchSchedules();
      } else {
        toast.error(result?.msg || "Erro ao recalcular");
      }
    } catch (err) {
      toast.error("Erro ao recalcular horas");
    }
    setLoadingAction(false);
  }

  function openGenerate() {
    setGenerateError("");
    setShowGenerateModal(true);
  }

  function closeGenerate() {
    if (loadingAction) return;
    setShowGenerateModal(false);
  }

  async function handleGenerate() {
    setGenerateError("");
    // Validar se o intervalo total tem ao menos 1 mês dentro da vigência
    if (contract) {
      const intervalCheck = rangeIntersectsContractAndProjectPeriod(
        contract,
        firstDayOfMonth(genStart.month, genStart.year),
        lastDayOfMonth(genEnd.month, genEnd.year)
      );
      if (!intervalCheck.valid) {
        setGenerateError(`Nenhum mês do intervalo está dentro da vigência. ${intervalCheck.reason}.`);
        return;
      }
    }

    setLoadingAction(true);
    try {
      const result = await paymentScheduleService.generate(
        contractId,
        genStart.month,
        genStart.year,
        genEnd.month,
        genEnd.year
      );
      if (result && !result.error) {
        toast.success(result.msg || "Cronogramas gerados!");
        setShowGenerateModal(false);
        fetchSchedules();
      } else {
        toast.error(result?.msg || "Erro ao gerar cronogramas");
      }
    } catch (err) {
      toast.error("Erro ao gerar cronogramas");
    }
    setLoadingAction(false);
  }

  function openNfUpload(schedule) {
    setSelectedScheduleForUpload(schedule);
    setShowNfUpload(true);
  }

  function uploadBlockReason(schedule) {
    return getUploadBlockReason(schedule, contract, noBank);
  }

  function isUploadAllowed(schedule) {
    return !uploadBlockReason(schedule);
  }

  const isHourly = contract?.remuneration_type === "HOURLY";

  function hoursCell(schedule) {
    const mode = schedule.hour_tracking_mode || contract?.hour_tracking_mode || "HOURS_ONLY";
    if (mode === FIXED_MODE) {
      // No valor fixo o número de horas sozinho não explica nada:
      // ao lado do valor esperado, convida a dividir um pelo outro.
      // O par que produz o saldo é horas no pagamento × jornada do mês
      // — os mesmos "Horas no pagamento" e "Jornada esperada" da aba de horas.
      const computed = schedule.paid_hours != null ? schedule.paid_hours : schedule.total_approved_hours;
      if (computed == null) return "-";
      if (schedule.expected_hours == null) return hrs(computed);
      return (
        <Tooltip
          content={`Horas no pagamento da competência / jornada esperada do mês.${hasLedger(schedule) ? ` ${LEDGER_RULE}` : ""}`}
          $position="top"
          $size={hasLedger(schedule) ? "large" : "medium"}
          $color="dark">
          <Text $text={`${hrs(computed)} / ${hrs(schedule.expected_hours)}`} $variant="paragraph_small_medium" $color="N900" />
        </Tooltip>
      );
    }
    if (mode === "ABSENCES_ONLY") {
      return schedule.total_absence_days != null ? `${schedule.total_absence_days} dias` : "-";
    }
    if (mode === "HOURS_AND_ABSENCES") {
      const hasHours = schedule.total_approved_hours != null;
      const hasAbsences = !!schedule.total_absence_days;
      if (!hasHours && !hasAbsences) return "-";
      return [hasHours ? hrs(schedule.total_approved_hours) : null, hasAbsences ? `${schedule.total_absence_days} dias aus.` : null]
        .filter(Boolean)
        .join(" / ");
    }
    return schedule.total_approved_hours != null ? hrs(schedule.total_approved_hours) : "-";
  }

  /** Para o prestador: o motivo do bloqueio do envio, com o atalho dos dados bancários. */
  function blockedText(schedule) {
    if (noBank) {
      return (
        <Stack $gap="2px" $align="flex-start">
          <Text $text="Adicione seus dados bancários para enviar" $variant="caption_medium" $color="E400" />
          <span onClick={stopRowClick}>
            <ButtonLink $text="Adicionar dados bancários" $action={() => navigate(bankDataRoute(location))} $noPadding $noWidth />
          </span>
        </Stack>
      );
    }
    const reason = uploadBlockReason(schedule);
    return reason ? <Text $text={reason} $variant="caption_medium" $color="E400" /> : null;
  }

  function sendButton(schedule, text) {
    return (
      <span onClick={stopRowClick}>
        <Button
          $text={text}
          $variant="outline"
          $size="small"
          $noWidth
          $isDisabled={!isUploadAllowed(schedule)}
          $action={() => openNfUpload(schedule)}
        />
      </span>
    );
  }

  function downloadButton(nf) {
    return (
      <span onClick={stopRowClick}>
        <ButtonRound title="Baixar nota fiscal" aria-label="Baixar nota fiscal" $variant="outline" $icon={<ExportIcon />} $noWidth $action={() => handleDownloadNf(nf)} />
      </span>
    );
  }

  function invoiceCell(schedule) {
    const nf = schedule.taxInvoice;
    if (nf) {
      return (
        <Stack $gap="8px" $align="flex-start">
          <Inline>
            <DomainStatus domain="notaFiscal" value={INVOICE_STATUS[nf.status] || nf.status} />
            {canDownloadInvoice ? downloadButton(nf) : null}
            {nf.status === "REJECTED" && nf.rejection_reason ? (
              <Tooltip content={`Motivo: ${nf.rejection_reason}`} $position="top" $size="medium" $color="dark">
                <IconBox aria-label="Ver motivo da recusa">
                  <AlertIcon />
                </IconBox>
              </Tooltip>
            ) : null}
          </Inline>
          {/* Prestador pode reenviar NF quando foi rejeitada */}
          {!isAdmin && nf.status === "REJECTED" ? (
            <>
              {sendButton(schedule, "Reenviar nota")}
              {!isUploadAllowed(schedule) ? blockedText(schedule) : null}
            </>
          ) : null}
        </Stack>
      );
    }
    /* Competencia encerrada nao recebe nota: nem botao apagado
       para o prestador, nem cobranca de nota para o admin. */
    if (schedule.status !== "PENDING") return "-";
    if (isAdmin) return <Text $text="Sem nota" $variant="paragraph_small_medium" $color="N400" />;
    return (
      <Stack $gap="8px" $align="flex-start">
        {sendButton(schedule, "Enviar nota")}
        {!isUploadAllowed(schedule) || noBank ? blockedText(schedule) : null}
      </Stack>
    );
  }

  function rowActions(schedule) {
    const nf = schedule.taxInvoice;
    const pending = schedule.status === "PENDING";
    const pendingNf = canManage && nf && nf.status === "PENDING";
    // Nota aprovada ainda pode ser recusada enquanto o pagamento está em aberto:
    // é como se corrige um valor que ela travou
    const approvedNf = canManage && pending && nf && nf.status === "APPROVED";
    const editable = canManage && pending && !schedule.tax_invoice_id;
    return [
      // Detalhes do cronograma, na tela do papel de quem esta olhando.
      { label: "Ver cronograma", action: () => navigate(`${isAdmin ? "/c" : "/e"}/payment-schedules/${schedule.id}`) },
      { label: "Aprovar nota", action: () => setNfToApprove({ ...nf, schedule }), show: !!pendingNf },
      { label: "Marcar como pago", action: () => handleMarkAsPaid(schedule), show: !!approvedNf },
      // Uma entrada só: a lib usa o label como key, e as escondidas também são renderizadas
      {
        label: "Recusar nota",
        action: () => setSelectedNfForReject({ ...nf, schedule }),
        show: !!(pendingNf || approvedNf),
        isImportant: true,
      },
      { label: "Recalcular horas", action: () => handleRecalculateHourly(schedule.id), show: editable && isHourly },
      { label: "Editar", action: () => setSelectedScheduleForEdit(schedule), show: editable },
      { label: "Excluir", action: () => setSelectedScheduleForDelete(schedule), show: editable, isImportant: true },
    ];
  }

  const columns = [
    {
      id: "competence",
      header: "Competência",
      size: 160,
      cell: ({ row }) => {
        const schedule = row.original;
        const period = schedulePeriod(schedule, contract);
        return (
          <Stack $gap="2px">
            <Text $text={formatCompetencia(competenceOf(schedule))} $variant="paragraph_small_bold" $color="N900" />
            {period ? <Text $text={`apura ${formatPeriodLabel(period.start, period.end)}`} $variant="caption_medium" $color="N400" /> : null}
          </Stack>
        );
      },
    },
    { id: "due_date", header: "Vencimento", size: 110, cell: ({ row }) => (row.original.due_date ? formatDate(row.original.due_date) : "-") },
    {
      id: "expected_value",
      header: "Valor esperado",
      size: 170,
      cell: ({ row }) => (
        <Inline>
          {row.original.expected_value != null && row.original.expected_value !== "" ? formatBRL(row.original.expected_value) : "-"}
          {/* A coluna Horas ao lado ja mostra o par horas no pagamento/jornada;
              aqui falta so dizer que o valor ainda nao fechou. */}
          {isCompetenceOpen(row.original) ? <StatePill $text="Parcial" $variant="orange" $size="small" /> : null}
          {fixedAdjustmentCompetence(row.original)?.isFrozen ? <StatePill $text="Encerrada" $variant="gray" $size="small" /> : null}
        </Inline>
      ),
    },
  ];
  if (isHourly) {
    columns.push({
      id: "hours",
      header:
        contract?.hour_tracking_mode === "ABSENCES_ONLY" ? "Ausências" : contract?.hour_tracking_mode === FIXED_MODE ? "Horas" : "Horas aprov.",
      size: 130,
      cell: ({ row }) => hoursCell(row.original),
    });
  }
  columns.push(
    {
      id: "actual_value",
      header: "Valor pago",
      size: 130,
      cell: ({ row }) => (Number(row.original.actual_value) ? formatBRL(row.original.actual_value) : "-"),
    },
    {
      id: "status",
      header: "Status",
      size: 110,
      cell: ({ row }) => <DomainStatus domain="pagamento" value={SCHEDULE_STATUS[row.original.status] || row.original.status} />,
    },
    { id: "invoice", header: "Nota fiscal", size: 200, maxSize: 240, cell: ({ row }) => invoiceCell(row.original) },
    {
      id: "actions",
      // O "Ações" da lib tem 70px; abaixo disso a seta sai da célula
      size: 80,
      minSize: 80,
      maxSize: 80,
      align: "right",
      cell: ({ row }) => <DropdownAction options={rowActions(row.original)} />,
    }
  );

  const filtered = !!statusFilter.value;
  const noResults = filtered ? (
    <NoResults notResultText="Nenhum cronograma com este status" includeText="Limpar filtro" includeAction={() => changeStatusFilter(STATUS_DATA[0])} />
  ) : (
    <NoResults notResultText="Nenhum cronograma de pagamento encontrado" />
  );

  const detailNf = detailSchedule?.taxInvoice;
  const detailPeriod = detailSchedule ? schedulePeriod(detailSchedule, contract) : null;
  const detailSubtitle = detailSchedule
    ? [
        detailPeriod ? `Apura ${formatPeriodLabel(detailPeriod.start, detailPeriod.end)}` : null,
        detailSchedule.due_date ? `vence em ${formatDate(detailSchedule.due_date)}` : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : "";

  const modalText = { $variant: "paragraph_small_medium", $color: "N700", $colorEmphasis: "N900" };

  return (
    <ContentPage>
      <Row $justifyContent="space-between" $alignItems="center" $gap={16}>
        <SelectorPageStatus options={STATUS_DATA} selected={statusFilter} onChange={changeStatusFilter} />
        {canManage ? (
          <Actions>
            <Button $text="Gerar cronograma" $variant="outline" $noWidth $action={openGenerate} />
            {contract?.remuneration_type === "VARIABLE" && contract?.status === "ACTIVE" ? (
              <Button $text="Informar valor" $variant="primary" $noWidth $action={() => setShowValueInput(true)} />
            ) : null}
          </Actions>
        ) : null}
      </Row>

      <FullTable
        data={schedules}
        columns={columns}
        rowCount={totalRegisters}
        pagination={pagination}
        setPagination={setPagination}
        getRowId={(row) => String(row.id)}
        isLoading={loading}
        onRowClick={openDetail}
        isRowClickable={(schedule) => Boolean(schedule.taxInvoice)}
        noResults={noResults}
      />

      {/* Drawer: nota fiscal da competência */}
      <Presence show={detailOpen}>
        {detailSchedule ? (
          <DrawerPanel $showDrawer={detailOpen} $setShowDrawer={() => setDetailOpen(false)} $direction="right" $size="panel">
            <Stack $gap="24px">
              <Stack $gap="4px">
                <Text $text={`Competência ${formatCompetencia(competenceOf(detailSchedule))}`} $variant="h4_bold" $color="N900" />
                {detailSubtitle ? <Text $text={detailSubtitle} $variant="caption_medium" $color="N400" /> : null}
              </Stack>
              {detailNf ? (
                <Stack $gap="16px">
                  <DescriptionList
                    $title="Nota fiscal"
                    $columns={1}
                    $items={[
                      {
                        label: "Arquivo",
                        value: (
                          <Inline>
                            {canDownloadInvoice ? (
                              <ButtonLink $text={detailNf.filename || "Nota fiscal"} $action={() => handleDownloadNf(detailNf)} $noPadding $noWidth />
                            ) : (
                              <Text $text={escapeHtml(detailNf.filename || "Nota fiscal")} $variant="paragraph_small_medium" $color="N900" />
                            )}
                            <DomainStatus domain="notaFiscal" value={INVOICE_STATUS[detailNf.status] || detailNf.status} />
                            {canDownloadInvoice ? downloadButton(detailNf) : null}
                          </Inline>
                        ),
                      },
                      { label: "Valor da nota", value: formatBRL(detailNf.value) },
                    ]}
                  />
                  {detailNf.status === "REJECTED" && detailNf.rejection_reason ? (
                    <Notice $variant="red" $title="Motivo da recusa" $text="%s" $replaces={[escapeHtml(detailNf.rejection_reason)]} />
                  ) : null}
                </Stack>
              ) : null}
            </Stack>
          </DrawerPanel>
        ) : null}
      </Presence>

      {/* Modal: Gerar Cronograma */}
      {showGenerateModal && (
        <ModalForm
          $showModal={showGenerateModal}
          $setShowModal={closeGenerate}
          $noClose={loadingAction}
          $isLoading={loadingAction}
          $size="medium"
          $title="Gerar cronograma de pagamento"
          $contentBackground="N0"
          $actions={[
            { text: "Cancelar", action: closeGenerate, variant: "outline", isDisabled: loadingAction },
            { text: "Gerar cronograma", action: handleGenerate, variant: "primary", isDisabled: loadingAction },
          ]}
        >
          <Stack $gap="20px">
            <Text $text="Cria uma competência para cada mês do intervalo que estiver dentro da vigência do contrato." {...modalText} />
            {generateError ? <Notice $variant="red" $text="%s" $replaces={[escapeHtml(generateError)]} /> : null}
            <CompetenceFields>
              <DatePicker
                $idForm="generate-start"
                $centralize
                $label="De"
                $variant="default"
                $isCompetence
                $isRequired
                $date={new Date(genStart.year, genStart.month - 1, 1)}
                $setDate={(date) => date && setGenStart({ month: date.getMonth() + 1, year: date.getFullYear() })}
              />
              <DatePicker
                $idForm="generate-end"
                $centralize
                $label="Até"
                $variant="default"
                $isCompetence
                $isRequired
                $date={new Date(genEnd.year, genEnd.month - 1, 1)}
                $setDate={(date) => date && setGenEnd({ month: date.getMonth() + 1, year: date.getFullYear() })}
              />
            </CompetenceFields>
          </Stack>
        </ModalForm>
      )}

      {/* Modal: Marcar como Pago */}
      {selectedScheduleForPaid && (
        <MarkPaidModal
          schedule={selectedScheduleForPaid}
          onClose={() => setSelectedScheduleForPaid(null)}
          onPaid={() => {
            setSelectedScheduleForPaid(null);
            fetchSchedules();
          }}
        />
      )}

      {/* Modal: Aprovar NF */}
      {nfToApprove && (
        <ApproveInvoiceModal
          invoice={nfToApprove}
          schedule={nfToApprove.schedule}
          onClose={() => setNfToApprove(null)}
          onApproved={() => {
            setNfToApprove(null);
            fetchSchedules();
          }}
        />
      )}

      {/* Modal: Recusar NF */}
      {selectedNfForReject && (
        <RejectInvoiceModal
          invoice={selectedNfForReject}
          schedule={selectedNfForReject.schedule}
          onClose={() => setSelectedNfForReject(null)}
          onRejected={() => {
            setSelectedNfForReject(null);
            fetchSchedules();
          }}
        />
      )}

      {/* Modal: Editar Cronograma */}
      {selectedScheduleForEdit && (
        <EditScheduleModal
          schedule={selectedScheduleForEdit}
          isHourly={isHourly}
          onClose={() => setSelectedScheduleForEdit(null)}
          onSaved={() => {
            setSelectedScheduleForEdit(null);
            fetchSchedules();
          }}
        />
      )}

      {/* Modal: Excluir Cronograma */}
      {selectedScheduleForDelete && (
        <DeleteScheduleModal
          schedule={selectedScheduleForDelete}
          onClose={() => setSelectedScheduleForDelete(null)}
          onDeleted={() => {
            setSelectedScheduleForDelete(null);
            fetchSchedules();
          }}
        />
      )}

      {/* Modal: Value Input (VARIABLE) */}
      {canManage && contract?.remuneration_type === "VARIABLE" && (
        <ValueInputModal
          show={showValueInput}
          onHide={() => setShowValueInput(false)}
          contract={contract}
          valueInputService={valueInputService}
          onSuccess={() => {
            toast.success(
              contract.requires_value_approval
                ? "Valor submetido e aguardando aprovação!"
                : "Valor aprovado automaticamente!"
            );
            fetchSchedules();
          }}
        />
      )}

      {/* Modal: NF Upload (EMP) */}
      {!isAdmin && (
        <TaxInvoiceUploadModal
          show={showNfUpload}
          onHide={() => { setShowNfUpload(false); setSelectedScheduleForUpload(null); }}
          schedule={selectedScheduleForUpload}
          nfeService={taxInvoicesService}
          onSuccess={() => {
            toast.success("Nota fiscal enviada com sucesso!");
            fetchSchedules();
          }}
        />
      )}
    </ContentPage>
  );
}
